import queryString from 'query-string';
import get from 'lodash/get';

import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_NICHE_DETAIL_SET_DATA,
  CATALOG_NICHE_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import { Niche } from 'rebook/apps/catalog/models';
import getFloorNumber from 'rebook/apps/utils/text/getFloorNumber';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const currentState = getState().nicheDetail;
    let newState = {};
    const promises = [];

    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }

    if (currentState.niche) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    dispatch(setData(newState));

    // partial niche state may be available from router
    // so we have to fetch the full object detail along with its genres
    if (!newState.niche || !newState.genres || newState.niche.seo_description === undefined) {
      await dispatch(fetchDetail(match));
    }

    if (!newState.books) {
      promises.push(dispatch(fetchBookList(match)));
    }

    if (!newState.booksets) {
      promises.push(dispatch(fetchBooksets(match)));
    }

    if (!newState.audiobooks) {
      promises.push(dispatch(fetchAudiobookList()));
    }

    if (promises.length > 0) {
      Promise.all(promises);
    }
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { nicheSlug } = match.params;
    const url = `/api/catalog/niches/s-${nicheSlug}/`;

    return fetchQuery(url, { headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          niche: data,
          // filter empty genres
          genres: data.genres.filter((genre) => genre.active_book_count > 0),
        }));
      })
      .catch(({ data, resp, error }) => {
        if (resp.status === 404) {
          dispatch(Error404Actions.show());
        }
        else {
          Raven.captureMessage('failed to load niche detail due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
              error,
            },
          });
          throw new Error(`failed to load niche detail due to server error. ${data}`);
        }
      });
  };
}

function fetchBookList(match) {
  return (dispatch, getState) => {
    const { bookType } = match.params;
    const { ssr, nicheDetail } = getState();
    const { niche } = nicheDetail;

    if (!niche) {
      return null;
    }

    let queryParams = {
      niches: niche.id,
      limit: 10,
      author_weight__gte: 0,
      ...(bookType === 'free' && { subscription_id: 0 }),
    };

    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          books: data.objects,
          totalBookCount: data.meta.total_count,
        }));
      })
      .catch(({ data, resp, error }) => {
        Raven.captureMessage('failed to load niche book list due to server error', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
            error,
          },
        });
        throw new Error('failed to load niche book list due to server error');
      });
  };
}

function fetchAudiobookList() {
  return async(dispatch, getState) => {
    const { ssr, nicheDetail } = getState();
    const { niche } = nicheDetail;

    if (!niche) {
      return null;
    }

    let queryParams = {
      niches: niche.id,
      limit: 10,
      author_weight__gte: 0,
      type: 'audio',
    };

    const qs = queryString.stringify(queryParams);
    const url = `/api/books/?${qs}`;

    return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          audiobooks: {
            books: data.objects,
            bookCount: data.meta.total_count,
          },
        }));
      })
      .catch(({ data, resp, error }) => {
        Raven.captureMessage('failed to load niche audio book list due to server error', {
          level: 'error',
          extra: {
            status: resp.status,
            response: data,
            error,
          },
        });
        throw new Error('failed to load niche audio book list due to server error');
      });
  };
}

function fetchBooksets() {
  return (dispatch, getState) => {
    const { nicheDetail, ssr } = getState();
    if (!get(nicheDetail, 'niche.id', null)) {
      return null;
    }
    const url = `/api/booksets/?limit=10&niche=${nicheDetail.niche.id}`;
    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(({ data }) => {
          dispatch(setData({
            booksets: data.objects,
          }));
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to load niche booksets list due to server error', {
            level: 'error',
            extra: {
              status: get(resp, 'status', null),
              response: data,
              error,
            },
          });
          throw new Error('failed to load niche booksets list due to server error');
        })
    );
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { payments, nicheDetail } = getState();
    const niche = new Niche(nicheDetail.niche);
    const subscriptionPrice = ['biznes-knigi'].indexOf(niche.slug) >= 0
      ? payments.prices.audio['1'].full_price
      : payments.prices.standard['1'].full_price;
    const title = `Жанр «${niche.name}» ` +
                  `— читать и слушать онлайн аудиокниги, последние поступления и популярные книги ` +
                  `в электронной библиотеке MyBook`;
    const description = `Книги и аудиокниги в жанре  «${niche.name}». ` +
                        `Новинки и бестселлеры жанра «${niche.name}», `+
                        `популярные авторы и лучшие рецензии в электронной библиотеке MyBook.`;
    const canonicalUrl = `https://${BASE_HOST}${niche.getUrl()}`;
    const defaultOgDescription = `${getFloorNumber(niche.active_book_count)} ` +
                                 `${niche.seo_head ? niche.seo_head : niche.name} ` +
                                 `всего за ${subscriptionPrice} рублей в месяц. ` +
                                 `Читать онлайн, на iPhone, iPad или Android бестселлеры ` +
                                 `${niche.seo_tail ? niche.seo_tail : niche.name}.`;
    const ogDescription = niche.open_graph ? niche.open_graph : defaultOgDescription;
    const ogTags = [
      { name: 'description', value: ogDescription },
    ];
    const ogTwitterTags = [
      { name: 'description', value: ogDescription },
    ];

    dispatch(SeoActions.setData({
      title,
      description,
      ogTags,
      ogTwitterTags,
      canonicalUrl,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { niche } = payload;

    dispatch({
      type: CATALOG_NICHE_DETAIL_SET_DATA,
      payload,
    });

    if (niche) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_NICHE_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
